import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { cartApi } from '@/api/orders'
import { shoppingListApi } from '@/api/shoppingList'
import { AppShell } from '@/components/AppShell'
import { toast } from '@/lib/toast'
import { IconCart, IconCheck } from '@/components/icons'
import { Alert, Button, EmptyState, SkeletonList } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { QuantityStepper } from '@/features/list/quantity'
import { useAuthStore } from '@/stores/authStore'

export default function CartPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const translateError = useApiError()
  const canSend = useAuthStore((s) => s.hasPermission('order:send'))
  const [moving, setMoving] = useState<string | null>(null)

  const cart = useQuery({ queryKey: ['cart'], queryFn: cartApi.get })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['cart'] })

  const updateItem = useMutation({
    mutationFn: ({ id, quantity }: { id: string; quantity: string }) =>
      cartApi.updateItem(id, { quantity }),
    onSuccess: () => void invalidate(),
  })
  const removeItem = useMutation({
    mutationFn: cartApi.removeItem,
    onSuccess: () => void invalidate(),
  })
  const clearCart = useMutation({
    mutationFn: cartApi.clear,
    onSuccess: () => {
      void invalidate()
      toast.success(t('cart.cleared'))
    },
  })

  // Η λίστα φορτώνεται εδώ μόνο για να ξέρουμε ποια είδη έχουν εναλλακτικό
  // προμηθευτή: χωρίς αυτό δεν μπορούμε να προσφέρουμε αλλαγή πριν την αποστολή.
  const list = useQuery({ queryKey: ['shopping-list', '', false], queryFn: () => shoppingListApi.get() })

  const alternativesFor = (name: string, unit: string) =>
    list.data?.items.find(
      (entry) => entry.name.toLocaleLowerCase('el') === name.toLocaleLowerCase('el') && entry.unit === unit,
    )?.options ?? []

  const moveItem = useMutation({
    mutationFn: ({ id, businessItemId }: { id: string; businessItemId: string; name: string }) =>
      cartApi.moveItem(id, businessItemId),
    onSuccess: (_, { name }) => {
      setMoving(null)
      void invalidate()
      toast.success(t('cart.movedTo', { name }))
    },
  })

  const isEmpty = cart.data?.itemCount === 0

  return (
    <AppShell title={t('nav.cart')}>
      {cart.isPending && <SkeletonList rows={3} />}
      {cart.isError && <Alert>{translateError(cart.error)}</Alert>}

      {isEmpty && (
        <EmptyState
          icon={<IconCart size={26} />}
          title={t('cart.empty')}
          action={
            <div className="flex flex-col gap-2">
              <Link
                to="/list"
                className="flex h-touch items-center justify-center rounded-xl bg-brand-700 font-semibold text-white shadow-card hover:bg-brand-600"
              >
                {t('cart.goToList')}
              </Link>
              <Link
                to="/templates"
                className="flex h-touch items-center justify-center rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                {t('cart.loadTemplate')}
              </Link>
            </div>
          }
        />
      )}

      {cart.data && !isEmpty && (
        <>
          {/* Σαφής ένδειξη πόσες παραγγελίες θα σταλούν (§11.5) */}
          <div className="mb-4 rounded-2xl border border-brand-200 bg-brand-50 p-4">
            <p className="text-sm font-semibold text-brand-900">
              {t('cart.willSend', { count: cart.data.orderCount })}
            </p>
            <p className="mt-1 text-xs text-brand-800">{t('cart.willSendHint')}</p>
            <p className="mt-1 text-xs text-brand-800">{t('cart.editHint')}</p>
          </div>

          <div className="flex flex-col gap-4">
            {cart.data.groups.map((group) => (
              <section
                key={group.supplierId}
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
              >
                <header className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                  <Link
                    to={`/suppliers/${group.supplierId}`}
                    className="font-semibold text-gray-900 hover:text-brand-700"
                  >
                    {group.supplierName}
                  </Link>
                  <p className="truncate text-xs text-gray-500">{group.supplierEmail}</p>
                </header>

                <ul className="divide-y divide-gray-100">
                  {group.items.map((item) => {
                    const alternatives = alternativesFor(item.name, item.unit)
                    return (
                    <li key={item.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-gray-900">{item.name}</p>
                        <LineNote itemId={item.id} note={item.note} onSaved={() => void invalidate()} />
                        {!item.isAvailableToday && (
                          <p className="text-xs font-medium text-amber-700">
                            {t('availability.missingToday')}
                          </p>
                        )}
                        {/* Η αλλαγή προσφέρεται μόνο όπου υπάρχει πραγματική
                            εναλλακτική: ένα κουμπί που δεν οδηγεί πουθενά είναι
                            χειρότερο από την απουσία του. */}
                        {alternatives.length > 1 && (
                          <button
                            onClick={() => setMoving(moving === item.id ? null : item.id)}
                            className="mt-1 rounded-lg bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-200"
                          >
                            {t('cart.changeSupplier')} ▾
                          </button>
                        )}
                      </div>

                      <span className="w-9 shrink-0 text-right text-xs text-gray-400">
                        {t(`units.${item.unit}`)}
                      </span>
                      {/* Ίδιο χειριστήριο με τη λίστα: ο χρήστης δεν χρειάζεται
                          να μάθει δύο τρόπους για το ίδιο πράγμα. Η ποσότητα
                          στο μηδέν σβήνει τη γραμμή. */}
                      <QuantityStepper
                        value={String(Number(item.quantity))}
                        unit={item.unit}
                        compact
                        onChange={(value) => {
                          if (Number(value) > 0) {
                            updateItem.mutate({ id: item.id, quantity: value })
                          } else {
                            removeItem.mutate(item.id)
                          }
                        }}
                      />

                      {moving === item.id && (
                        <ul className="w-full rounded-xl bg-gray-50 p-1.5">
                          {alternatives.map((option) => (
                            <li key={option.supplierId}>
                              <button
                                disabled={option.supplierId === group.supplierId}
                                onClick={() =>
                                  moveItem.mutate({
                                    id: item.id,
                                    businessItemId: option.businessItemId,
                                    name: option.supplierName,
                                  })
                                }
                                className={
                                  'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ' +
                                  (option.supplierId === group.supplierId
                                    ? 'font-semibold text-brand-800'
                                    : 'text-gray-700 hover:bg-white')
                                }
                              >
                                <span className="min-w-0 flex-1 truncate">
                                  {option.supplierName}
                                  {!option.isAvailableToday && (
                                    <span className="ml-2 text-xs font-normal text-amber-700">
                                      {t('list.unavailable')}
                                    </span>
                                  )}
                                </span>
                                {option.price && (
                                  <span className="shrink-0 tabular-nums text-gray-500">
                                    {Number(option.price).toFixed(2)} €
                                  </span>
                                )}
                                {option.supplierId === group.supplierId && (
                                  <IconCheck size={16} className="shrink-0" />
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                    )
                  })}
                </ul>
              </section>
            ))}
          </div>

          <div className="mt-5 flex flex-col gap-2">
            {canSend ? (
              <Link
                to="/checkout"
                className="btn inline-flex w-full items-center justify-center rounded-xl bg-brand-700 px-5 text-base font-semibold text-white hover:bg-brand-600"
              >
                {t('cart.continue')}
              </Link>
            ) : (
              <Alert>{t('cart.staffCannotSend')}</Alert>
            )}

            <Link
              to="/templates"
              className="btn inline-flex w-full items-center justify-center rounded-xl border border-gray-300 bg-white px-5 text-base font-semibold text-gray-800 hover:bg-gray-50"
            >
              {t('cart.saveAsTemplate')}
            </Link>

            <Button
              variant="secondary"
              loading={clearCart.isPending}
              onClick={() => clearCart.mutate()}
            >
              {t('cart.clear')}
            </Button>
          </div>
        </>
      )}
    </AppShell>
  )
}



/**
 * Παρατήρηση για τον προμηθευτή σε μία γραμμή, π.χ. «καλοψημένο» ή «όχι
 * μεγάλα». Φτάνει στο email και στη σελίδα της παραγγελίας δίπλα στο είδος.
 */
function LineNote({
  itemId,
  note,
  onSaved,
}: {
  itemId: string
  note: string | null
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(note ?? '')

  const save = useMutation({
    mutationFn: () => cartApi.updateItem(itemId, { note: value.trim() }),
    onSuccess: () => {
      setEditing(false)
      onSaved()
    },
  })

  if (editing) {
    return (
      <form
        className="mt-1 flex gap-1.5"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate()
        }}
      >
        <input
          value={value}
          autoFocus
          maxLength={500}
          onChange={(event) => setValue(event.target.value)}
          placeholder={t('cart.notePlaceholder')}
          aria-label={t('cart.notePlaceholder')}
          className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 px-2.5 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
        />
        <button
          type="submit"
          disabled={save.isPending}
          className="h-10 shrink-0 rounded-lg bg-brand-700 px-3 text-sm font-semibold text-white disabled:opacity-60"
        >
          {t('common.save')}
        </button>
      </form>
    )
  }

  return note ? (
    <button
      onClick={() => {
        setValue(note)
        setEditing(true)
      }}
      className="block max-w-full truncate text-left text-xs italic text-gray-600 hover:text-brand-700"
    >
      {note}
    </button>
  ) : (
    <button
      onClick={() => setEditing(true)}
      className="text-xs font-semibold text-gray-400 hover:text-brand-700"
    >
      {t('cart.addNote')}
    </button>
  )
}
